import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { LogOut, LayoutDashboard, Crown, Check, Clock, ArrowLeft, Upload, Image as ImageIcon, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const Account = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [signups, setSignups] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", city: "", payment_ref: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [screenshot, setScreenshot] = useState(null); // {file_id, previewUrl, name}
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/scheme/my-signups");
      setSignups(res.data);
    } catch (e) {}
  };

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name }));
    load();
  }, [user]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast({ title: "File too large", description: "Please upload an image under 8MB." });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/payment-screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScreenshot({ file_id: res.data.file_id, previewUrl: URL.createObjectURL(file), name: file.name });
      toast({ title: "Screenshot uploaded", description: "Attached to your application." });
    } catch (err) {
      toast({ title: "Upload failed", description: "Please try a different image." });
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Missing details", description: "Name and phone are required." });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/scheme/signup", { ...form, screenshot_id: screenshot?.file_id || null });
      toast({ title: "Application submitted", description: "We'll confirm your ₹65,000 payment shortly." });
      setForm({ name: user?.name || "", phone: "", city: "", payment_ref: "" });
      setScreenshot(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast({ title: "Something went wrong", description: "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s) => {
    if (s === "confirmed") return <span className="inline-flex items-center gap-1 bg-gold/15 text-gold font-ui text-[10px] tracking-wider uppercase px-3 py-1.5"><Check size={12} /> Confirmed</span>;
    return <span className="inline-flex items-center gap-1 bg-white/8 text-gray-300 font-ui text-[10px] tracking-wider uppercase px-3 py-1.5"><Clock size={12} /> Pending</span>;
  };

  return (
    <div className="min-h-screen bg-ink grid-bg">
      <div className="max-w-[1000px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-ui text-[11px] tracking-[0.2em] uppercase text-gray-400 hover:text-gold transition-colors"><ArrowLeft size={14} /> Back to site</Link>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <Link to="/admin" className="flex items-center gap-2 border border-gold text-gold font-ui text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-gold hover:text-ink transition-colors"><LayoutDashboard size={14} /> Admin</Link>
            )}
            <button onClick={logout} className="flex items-center gap-2 border border-white/15 text-gray-300 font-ui text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 hover:border-gold hover:text-gold transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-10">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full border border-gold/40" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center font-display text-2xl text-gold">{user?.name?.charAt(0)}</div>
          )}
          <div>
            <h1 className="font-display smallcaps text-4xl text-white">Welcome, {user?.name?.split(" ")[0]}</h1>
            <p className="font-body text-gray-400">{user?.email}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {/* Join scheme */}
          <div className="border border-gold/25 bg-gradient-to-br from-gold/[0.07] to-transparent p-8">
            <div className="flex items-center gap-2 text-gold"><Crown size={22} /><h2 className="font-display text-2xl text-white">Join Golden Reward Scheme</h2></div>
            <p className="font-body text-gray-400 mt-2">One-time payment of <span className="text-gold font-display text-xl">₹65,000</span>. Pay via UPI/QR, then submit your reference below — our team confirms it.</p>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" className="ac-input" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone Number *" className="ac-input" />
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="ac-input" />
              <input value={form.payment_ref} onChange={(e) => setForm({ ...form, payment_ref: e.target.value })} placeholder="UPI / Payment Reference (optional)" className="ac-input" />

              <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={onPickFile} className="hidden" />
              {!screenshot ? (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 border border-dashed border-gold/40 bg-white/[0.02] text-gray-300 font-ui text-[11px] tracking-[0.15em] uppercase py-4 hover:border-gold hover:text-gold transition-colors disabled:opacity-60">
                  <Upload size={16} /> {uploading ? "Uploading…" : "Upload Payment Screenshot"}
                </button>
              ) : (
                <div className="flex items-center gap-3 border border-gold/30 bg-white/[0.03] p-3">
                  {screenshot.previewUrl && screenshot.name?.match(/\.(png|jpe?g|gif|webp)$/i) ? (
                    <img src={screenshot.previewUrl} alt="screenshot" className="h-12 w-12 object-cover rounded" />
                  ) : (
                    <div className="h-12 w-12 rounded bg-gold/10 flex items-center justify-center text-gold"><ImageIcon size={20} /></div>
                  )}
                  <span className="flex-1 font-body text-sm text-gray-300 truncate">{screenshot.name}</span>
                  <button type="button" onClick={() => { setScreenshot(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-gray-400 hover:text-destructive"><X size={18} /></button>
                </div>
              )}

              <button type="submit" disabled={submitting} className="w-full gold-btn font-ui text-xs tracking-[0.2em] uppercase py-4 font-medium disabled:opacity-60">{submitting ? "Submitting…" : "Submit Scheme Application"}</button>
            </form>
          </div>

          {/* My signups */}
          <div className="border border-white/10 bg-white/[0.02] p-8">
            <h2 className="font-display text-2xl text-white">My Applications</h2>
            {signups.length === 0 ? (
              <p className="font-body italic text-gray-500 mt-4">No applications yet. Join the scheme to get started!</p>
            ) : (
              <div className="mt-5 space-y-3">
                {signups.map((s) => (
                  <div key={s.id} className="border border-white/8 bg-black/20 p-4 flex items-center justify-between">
                    <div>
                      <p className="font-display text-lg text-white">₹{s.amount?.toLocaleString("en-IN")} · {s.name}</p>
                      <p className="font-body text-gray-500 text-sm">{s.city || "—"} · Ref: {s.payment_ref || "—"}</p>
                    </div>
                    {statusBadge(s.status)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .ac-input { width:100%; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.1); padding:0.75rem 1rem; color:#fff; font-family:'Poppins',sans-serif; outline:none; transition:border-color .2s; }
        .ac-input:focus { border-color:#d4af37; }
      `}</style>
    </div>
  );
};

export default Account;
